import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { LeoWordmark } from "@libs/zapi-ui/ds/components/leo-wordmark";
import {
  AiMessage,
  ChatShell,
  ProjectGenerationProgress,
  TypingDots,
  UserMessage
} from "@/zapi-frontend/shared/ui/chat";
import type { TGenerationProgressProps } from "@/zapi-frontend/widgets/ChatIsland/ui/ChatMessageList";
import { MessageMarkdown } from "@/zapi-frontend/shared/ui/custom/ChatMessage/MessageMarkdown";
import { isLeoChangingContent, type TLeoChatMessage } from "@/zapi-frontend/stores/leoChatStore";
import { useModelLedger } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ModelLedgerIsland/useModelLedger";
import { FigureRefsProvider } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/MemoIsland/FigureRefs/FigureRefsProvider";
import { useLeoChat } from "../hooks/useLeoChat";
import { readerMessages, replyMarkdown, replySlots } from "../lib/leoHistory";
import { LeoBusyMessage } from "./LeoBusyMessage";
import { LeoWelcome } from "./LeoWelcome";

type TLeoChatProps = {
  projectId: string;
  headerRight?: ReactNode;
  canEdit?: boolean;
  slideIndex?: number;
  generationProgress?: TGenerationProgressProps;
};

function KeyedReply({ projectId, message }: { projectId: string; message: TLeoChatMessage }) {
  const ledger = useModelLedger(projectId);
  const figures = ledger.data?.figures;
  const byKey = useMemo(() => new Map((figures ?? []).map((figure) => [figure.key, figure])), [figures]);
  if (ledger.isLoading) return <TypingDots />;
  return (
    <FigureRefsProvider figures={figures}>
      <MessageMarkdown content={replyMarkdown(message, byKey)} />
    </FigureRefsProvider>
  );
}

export function LeoChat({ projectId, headerRight, canEdit = true, slideIndex, generationProgress }: TLeoChatProps) {
  const { t } = useTranslation("projectHome");
  const {
    messages,
    activeTask,
    isBusy,
    status,
    send,
    stop,
    clear
  } = useLeoChat(projectId, {
    canEdit,
    slideIndex
  });

  const isGenerating = !!generationProgress;
  const isChangingContent = isLeoChangingContent(activeTask);

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isBusy, status, isChangingContent]);

  const submit = async () => {
    const text = input.trim();
    if (!text || isBusy || isGenerating) return;
    setInput("");
    const unsent = await send(text);
    if (unsent) setInput(unsent);
  };

  return (
    <ChatShell
      appearance="leo"
      data-testid="leo-chat-panel"
      className="h-full w-full"
      title={t("leo.title")}
      titleMark={<LeoWordmark className="h-[15px] w-auto text-leo" title={t("leo.title")} />}
      headerRight={headerRight}
      placeholder={isGenerating ? t("leo.placeholderGenerating") : t("leo.placeholderAnything")}
      input={input}
      onInputChange={setInput}
      onSubmit={() => void submit()}
      submitLabel={t("leo.send")}
      clearConfirm={{
        menuLabel: t("leo.clear"),
        title: t("leo.clearTitle"),
        cancelText: t("leo.cancel"),
        confirmText: t("leo.clear"),
        toastText: t("leo.chatCleared")
      }}
      isBusy={isBusy}
      isComposerDisabled={isGenerating}
      onStop={() => void stop()}
      stopTestId="leo-chat-stop"
      inputTestId="leo-chat-input"
      sendTestId="leo-chat-send"
      clearTestId="leo-chat-clear"
      onClear={clear}
      canClear={messages.length > 0 && !isBusy}
      scrollRef={scrollRef}
      footnote={t("leo.disclaimer")}
      dockCloseLabel={t("leo.hide")}
    >
      {generationProgress ? (
        <ProjectGenerationProgress {...generationProgress} />
      ) : (
        messages.length === 0 && (
          <LeoWelcome
            title={t("leo.welcomeTitle")}
            subtitle={t("leo.welcomeSubtitle")}
            suggestions={[t("leo.suggestionStands"), t("leo.suggestionUnproven"), t("leo.suggestionNext")]}
            onPick={(suggestion) => void send(suggestion)}
            disabled={isBusy}
          />
        )
      )}

      {readerMessages(messages).map(({ message, chaptersOnly }, index) => {
        if (chaptersOnly) {
          return (
            <AiMessage key={index} className="w-full">
              <FigmaTypography variant="body-3" color="zinc500">
                {t("leo.updatedChapters", { chapters: (message.appliedChapters ?? []).join(", ") })}
              </FigmaTypography>
            </AiMessage>
          );
        }
        if (message.role === "user") {
          return (
            <UserMessage key={index}>
              <span className="whitespace-pre-wrap">{message.content}</span>
            </UserMessage>
          );
        }
        return (
          <AiMessage key={index} state={message.isError ? "error" : "default"} className="w-full">
            {replySlots(message) ? (
              <KeyedReply projectId={projectId} message={message} />
            ) : (
              <MessageMarkdown content={message.content} />
            )}
            {!!message.appliedChapters?.length && (
              <FigmaTypography variant="body-3" color="zinc500">
                {t("leo.updatedChapters", { chapters: message.appliedChapters.join(", ") })}
              </FigmaTypography>
            )}
          </AiMessage>
        );
      })}

      {isBusy && <LeoBusyMessage status={status} isChangingContent={isChangingContent} />}
    </ChatShell>
  );
}
